/* Mirrors the Upcart rewards banner into the #bbt-free-shipping-slot div
   (rendered by Upcart's "above footer" custom HTML field) so it sits above
   the checkout button. Upcart keeps owning all amounts/logic; if the slot
   or module is missing, nothing happens and the stock banner shows at the
   top of the drawer. Some stores render Upcart's whole drawer inside an
   open shadow root on #upCart (store setting "renderCartInShadowDom") --
   when that's the case, every query and our injected stylesheet must live
   inside that shadow root instead of the page document.
   Spec: docs/superpowers/specs/2026-07-28-upcart-shipping-bar-design.md */
(function () {
  var SLOT_ID = 'bbt-free-shipping-slot';
  var ACTIVE_CLASS = 'bbt-fsb-active';
  var MODULE_SELECTOR =
    '.upcart-cart-body .upcart-rewards, .upcart-cart-body .upcart-public-rewards';
  var MESSAGE_SELECTOR = '.upcart-rewards-message, .upcart-public-rewards__message';
  var FILL_SELECTOR =
    '.upcart-rewards-bar-foreground, .upcart-public-component-segmented-progress-bar__segment-fill';
  var SHADOW_CSS_ID = 'bbt-fsb-shadow-css';
  var OBSERVE_CONFIG = {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  };
  var POLL_MS = 500;
  var POLL_MAX = 60;

  var lastHtml = null;
  var scheduled = false;
  var currentRoot = document;
  var bodyObserver = null;
  var pollTimer = null;

  /* Upcart clones its own stylesheet into the shadow root the same way --
     mirror that so our injected copy of the module can actually be styled. */
  function injectShadowCss(root) {
    if (root === document) return;
    if (root.querySelector('#' + SHADOW_CSS_ID)) return;
    var pageLink = document.querySelector('link[href*="upcart-shipping-bar.css"]');
    if (!pageLink) return;
    var clone = pageLink.cloneNode(false);
    clone.id = SHADOW_CSS_ID;
    root.appendChild(clone);
  }

  /* Patches the slot's existing mirrored copy in place instead of replacing
     it wholesale, so a CSS transition on the fill's width actually plays:
     transitions require a computed-value change on the SAME element, and
     `slot.innerHTML = html` always inserts a brand-new element already at
     its final value (which is why the transition used to be dead code).
     Returns false if the copy's structure doesn't match the source closely
     enough to patch safely -- caller falls back to a wholesale replace. */
  function patchInPlace(module, slot) {
    var srcMessage = module.querySelector(MESSAGE_SELECTOR);
    var copyMessage = slot.querySelector(MESSAGE_SELECTOR);
    if (!srcMessage || !copyMessage) return false;

    var srcFills = module.querySelectorAll(FILL_SELECTOR);
    var copyFills = slot.querySelectorAll(FILL_SELECTOR);
    if (srcFills.length !== copyFills.length) return false;

    if (copyMessage.innerHTML !== srcMessage.innerHTML) {
      copyMessage.innerHTML = srcMessage.innerHTML;
    }
    for (var i = 0; i < srcFills.length; i++) {
      var srcWidth = srcFills[i].style.width;
      if (copyFills[i].style.width !== srcWidth) {
        copyFills[i].style.width = srcWidth;
      }
    }
    return true;
  }

  function sync() {
    scheduled = false;
    var popup = currentRoot.querySelector('#CartPopup');
    if (!popup) return;

    /* Self-heal + degrade the shadow stylesheet: if it never arrived (or
       Upcart rebuilt its shadow root and wiped our clone), the hide/:empty/
       skin rules the CSS provides don't exist, so mirroring anyway would
       show a duplicate, unstyled banner alongside the native one. Re-inject
       first (cheap: injectShadowCss no-ops once the link is present, and
       this makes us self-heal if Upcart rebuilds its shadow); if the
       page-level link is gone too, degrade to the single native banner. */
    if (currentRoot !== document) {
      injectShadowCss(currentRoot);
      if (!currentRoot.querySelector('#' + SHADOW_CSS_ID)) {
        var slotNoCss = currentRoot.querySelector('#' + SLOT_ID);
        if (slotNoCss && slotNoCss.innerHTML !== '') slotNoCss.innerHTML = '';
        if (popup.classList.contains(ACTIVE_CLASS)) popup.classList.remove(ACTIVE_CLASS);
        lastHtml = null;
        return;
      }
    }

    var slot = currentRoot.querySelector('#' + SLOT_ID);
    var module = popup.querySelector(MODULE_SELECTOR);
    /* Upcart toggles inline display:none on the module when it should be hidden
       (e.g. hide-on-empty-cart) — treat that the same as absent. */
    if (!slot || !module || module.style.display === 'none') {
      if (slot && slot.innerHTML !== '') slot.innerHTML = '';
      if (popup.classList.contains(ACTIVE_CLASS)) popup.classList.remove(ACTIVE_CLASS);
      lastHtml = null;
      return;
    }

    var html = module.outerHTML;
    if (slot.innerHTML === '') {
      /* Fresh mount or drawer remount -- nothing to patch against or
         animate from; a wholesale write is correct here. */
      slot.innerHTML = html;
      lastHtml = html;
    } else if (html !== lastHtml) {
      /* Content actually changed and a previous copy exists -- try to patch
         it in place so the CSS transition plays; fall back to wholesale
         replace if the structure doesn't match closely enough to patch. */
      if (!patchInPlace(module, slot)) {
        slot.innerHTML = html;
      }
      lastHtml = html;
    }

    /* Guard the class mutation: the observer watches `class` in subtree, so
       an unconditional add/remove here would re-trigger schedule() every
       frame for as long as the drawer exists. */
    if (!popup.classList.contains(ACTIVE_CLASS)) popup.classList.add(ACTIVE_CLASS);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(sync);
  }

  function stopPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function stopWaiting() {
    if (bodyObserver) {
      bodyObserver.disconnect();
      bodyObserver = null;
    }
    stopPoll();
  }

  /* Resolves where the drawer actually lives and starts observing it.
     (a) #upCart has an open shadow root — the whole drawer (including
         #CartPopup) renders inside it; observe the shadow root itself,
         since React can unmount/remount #CartPopup within it.
     (b) otherwise, plain #CartPopup in the page document (non-shadow
         stores) — observe it directly, as before.
     Returns true once attached. */
  function tryAttach() {
    var up = document.getElementById('upCart');
    if (up && up.shadowRoot) {
      currentRoot = up.shadowRoot;
      injectShadowCss(currentRoot);
      new MutationObserver(schedule).observe(currentRoot, OBSERVE_CONFIG);
      schedule();
      return true;
    }
    var popup = document.getElementById('CartPopup');
    if (popup) {
      currentRoot = document;
      new MutationObserver(schedule).observe(popup, OBSERVE_CONFIG);
      schedule();
      return true;
    }
    return false;
  }

  function init() {
    if (!window.MutationObserver || !window.requestAnimationFrame) return;
    if (tryAttach()) return;

    /* Upcart injects the drawer after page load — wait for it. Catches a
       non-shadow drawer appearing late, and #upCart itself appearing late.
       Left running indefinitely until attach succeeds (unlike the capped
       poll below) — this is the pre-existing, unbounded wait behavior. */
    bodyObserver = new MutationObserver(function () {
      if (tryAttach()) stopWaiting();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    /* attachShadow() called on an #upCart that already exists produces no
       mutation the body observer above can see — poll for that case. Capped:
       once exhausted, only the poll stops; the body observer keeps waiting
       indefinitely for a non-shadow drawer or a late #upCart. */
    var pollCount = 0;
    pollTimer = setInterval(function () {
      pollCount++;
      if (tryAttach()) {
        stopWaiting();
        return;
      }
      if (pollCount >= POLL_MAX) stopPoll();
    }, POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
